import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import DateFormatter from "../../../app/formatters/DateFormatter";
import { getReadableTextColor } from "../../../app/formatters/ColorFormatter";
import moment from 'moment';

/** @typedef { import('../../../app/services/TaskService.js').TaskColumnObj } TaskColumnObj */
/** @typedef { import('../../../app/models/TaskLinkModel.js').TaskLinkItem } TaskLinkItem */

/**
 * @typedef {object} GwTaskItemProps
 * @property {TaskLinkItem} taskLink - Lien de tâche
 * @property {TaskColumnObj} taskColumn - Colonne de tâches
 * @property {function():void} onClickOnDeleteBtn - Fonction appelée lors du clic sur le bouton de suppression d'une tâche
 * @property {boolean} isDraggingOverlay - Indique si l'élément est en cours de déplacement (dragging overlay)
 * @property {string} avatarApiUrl - URL de l'API pour les avatars des utilisateurs
 */

/**
 * @param {GwTaskItemProps} props
 */
export default function GwTaskItem({taskLink, taskColumn, onClickOnDeleteBtn, isDraggingOverlay, avatarApiUrl}) {

  const [showNotes, setShowNotes] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: isDraggingOverlay ? `overlay-${taskLink.id}` : taskLink.id,
    data: { taskColumnId: taskLink.state },
    disabled: isDraggingOverlay
  });

  var transformedTaskLink = {
        ...taskLink,
        user: (taskLink.user) ? taskLink.user : ((taskLink.task) ? taskLink.task?.user : ''),
        description: (taskLink.description) ? taskLink.description : ((taskLink.task) ? `🔗${taskLink.task?.description}` : ''),
        notes: (taskLink.notes) ? taskLink.notes : ((taskLink.task) ? taskLink.task?.notes : ''),
        external_link: taskLink.external_link ?? taskLink.task?.external_link,
        taskPhotos: taskLink.task_photos,
        taskLinkTags: taskLink.task_link_tags
      },
      editHref = `/_edit/task-link/${transformedTaskLink.id}?onlyfields=userId,description,notes,external_link,deadline_date&model=${transformedTaskLink.model}&data_id=${transformedTaskLink.data_id}`,
      addPhotoHref = `/_addmultiple/task-photo?includingId=${transformedTaskLink.id}&includingModel=task-link`,
      addTagHref = `/_add/task-link-tag?includingId=${transformedTaskLink.data_id}&subIncludingProperty=taskBoardId&taskLinkId=${transformedTaskLink.id}`,
      avatarUrl = transformedTaskLink.user ? avatarApiUrl.replace('[value]', `${transformedTaskLink.user.firstname}+${transformedTaskLink.user.lastname}`) : '',
      notesClassName = showNotes ? 'task__item-notes task__item-notes--show' : 'task__item-notes',
      itemClassName = isDragging || isDraggingOverlay ? 'task__item task__item--dragging' : 'task__item',
      headerClassName = transformedTaskLink.user ? 'task__item-header' : 'task__item-header task__item-header--no-user',
      style = {
        transform: transform && !isDraggingOverlay ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition: isDraggingOverlay ? undefined : transition
      },
      dateClassName;

  switch(taskColumn.id) {
    case 0:
      dateClassName = transformedTaskLink.deadline_date && moment().isSameOrAfter(transformedTaskLink.deadline_date) ? 'task__item-date task__item-date--late' : 'task__item-date';
      break;
    case 1:
      dateClassName = 'task__item-date task__item-date--inprogress';
      break;
    case 2:
      dateClassName = 'task__item-date task__item-date--done';
      break;
  }


  transformedTaskLink.external_link = transformedTaskLink.external_link?.split(';') || [];

  const handleToggleNotes = () => {
    setShowNotes(!showNotes);
  };

  /**
   * Lors du clic sur le bouton pour supprimer une photo
   * @param {import('react').MouseEvent} event - Événement de clic sur le bouton pour supprimer une photo
  */
  const onClickOnDeletePhotoBtn = (event) => {
    if(!confirm('Voulez-vous vraiment supprimer cette photo ?')) {
      event.preventDefault();
    }
  }
  
  return (
    <div className={itemClassName} ref={setNodeRef} style={style} {...attributes}>
      <div className={headerClassName}>
        {transformedTaskLink.user ? (
          <img src={avatarUrl} className="task__item-avatar" data-tooltip={transformedTaskLink.user.firstname} alt="Avatar" />
        ) : ''}
        <div className="task__item-infos">
          <p className="task__item-description">{transformedTaskLink.description}</p>
          {transformedTaskLink.deadline_date ? (
            <p className={dateClassName}>{DateFormatter.formatDate(transformedTaskLink.deadline_date, true, false, false)}</p>
          ) : (
            ''
          )}
        </div>
        <div className="task__item-actions">
          {transformedTaskLink.notes && transformedTaskLink.state <= 1 ? (
            <button className="btn btn--only-svg" data-tooltip="Notes" onClick={handleToggleNotes}>
              <div className="material-icons material-symbols-outlined">expand_content</div>
            </button>
          ) : (
            ''
          )}
          {transformedTaskLink.external_link.map((externalLink, index) => (
            <a className="btn btn--only-svg" data-tooltip="Lien externe" href={externalLink} target="_blank" key={index}>
              <div className="material-icons material-symbols-outlined">link</div>
            </a>
          ))}
          {transformedTaskLink.state <= 1 ? (
            <a href={editHref} className="btn btn--only-svg">
              <div className="material-icons">edit</div>
            </a>
          ) : (
            ''
          )}
          {transformedTaskLink.state <= 1 ? (
            <a data-tooltip="Ajouter une image" href={addPhotoHref} className="btn btn--only-svg">
              <div className="material-icons">add_photo_alternate</div>
            </a>
          ) : (
            ''
          )}
          {transformedTaskLink.state <= 1 ? (
            <a data-tooltip="Ajouter un tag" href={addTagHref} className="btn btn--only-svg">
              <div className="material-icons">numbers</div>
            </a>
          ) : (
            ''
          )}
          <button className="btn btn--only-svg btn--only-svg-danger-color" onClick={() => onClickOnDeleteBtn()}>
            <div className="material-icons">delete</div>
          </button>
          <button className="btn btn--only-svg-secondary-color" {...listeners}>
            <div className="material-icons">drag_handle</div>
          </button>
        </div>
      </div>
      {transformedTaskLink.taskPhotos && transformedTaskLink.taskPhotos.length && transformedTaskLink.state <= 1 ? (
        <div className="task__item-photos">
          {transformedTaskLink.taskPhotos.map((taskPhoto, index) => (
            <div className="task__item-photo" key={index}>
              <a data-tooltip="Supprimer l'image" className="task__item-delete-btn" href={`/_delete/task-photo/${taskPhoto.id}`} onClick={onClickOnDeletePhotoBtn}>
                <div className="material-icons">close</div>
              </a>
              <a className="task__item-link" href={taskPhoto.name} target="_blank">
                <img src={taskPhoto.name} className="task__item-image" alt={taskPhoto.title} />
              </a>
            </div>
          ))}
        </div>
      ) : (
        ''
      )}
      {transformedTaskLink.taskLinkTags?.length && transformedTaskLink.state <= 1 ? (
        <div className="task__item-tags">
          <div className="task__item-tags-list">
            {transformedTaskLink.taskLinkTags.map((taskLinkTag, index) => (
              taskLinkTag.task_board_tag.color ? (
                <span className="task__item-tag" key={index} style={{ backgroundColor: taskLinkTag.task_board_tag.color, color: getReadableTextColor(taskLinkTag.task_board_tag.color) }}>{taskLinkTag.task_board_tag.name}</span>
              ) : (
                <span className="task__item-tag" key={index}>{taskLinkTag.task_board_tag.name}</span>
              )
            ))}
          </div>
          <a data-tooltip="Voir les tags" className="task__item-tag-btn" href={`/_index/task-link-tag?search[taskLinkId]=${transformedTaskLink.id}`}>
            <span className="material-icons">chevron_right</span>
          </a>
        </div>
      ) : (
        ''
      )}
      {transformedTaskLink.notes && transformedTaskLink.state <= 1 ? (
        <div className={notesClassName} dangerouslySetInnerHTML={{ __html: transformedTaskLink.notes }}></div>
      ) : (
        ''
      )}
    </div>
  )
}
